/**
 * MealTemplates page – admin-only page for creating and managing meal templates.
 * Route: /meal-templates
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { usePermissions } from "@/_core/hooks/usePermissions";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Search, BookOpen, Tag, X } from "lucide-react";
import { TemplateFormDialog } from "./components/TemplateFormDialog";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DraftItem {
  potravinaId: number;
  nazov: string;
  hmotnost: string;
}

// ─── Sub-component: Template form dialog ─────────────────────────────────────

interface TemplateFormDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Existing template id when editing; undefined when creating */
  editId?: number;
  initialNazov?: string;
  initialPoznamka?: string;
  initialJeVerejny?: boolean;
  initialItems?: DraftItem[];
  initialTagIds?: number[];
  potraviny: { id: number; nazov: string }[];
  allTags: { id: number; nazov: string; farba: string | null }[];
  canManageTags: boolean;
  canManageGlobalTemplates: boolean;
  onSaved: () => void;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function MealTemplatesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { canManageTags, canWriteOwnTemplates, canManageGlobalTemplates } =
    usePermissions();
  const currentClientId = user?.clientId ?? null;
  const canCreateTemplates = canWriteOwnTemplates || canManageGlobalTemplates;
  const [formOpen, setFormOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<{
    id: number;
    nazov: string;
    poznamka: string | null;
    klientId: number | null;
    jeVerejny: boolean;
    items: DraftItem[];
    tagIds: number[];
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const { data: templates = [], isLoading } = trpc.sablona.list.useQuery({});
  const { data: potraviny = [] } = trpc.potravina.list.useQuery();
  const { data: allTags = [] } = trpc.tag.list.useQuery();
  const templateIds = templates.map(tmpl => tmpl.id);
  const { data: templateTagsMap = {} } = trpc.tag.getForSablonaList.useQuery(
    { sablonaIds: templateIds },
    { enabled: templateIds.length > 0 }
  );
  const deleteMutation = trpc.sablona.delete.useMutation();
  const utils = trpc.useUtils();

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteId });
      toast.success(t("mealLibrary:sablona.deleted", "Template deleted"));
      utils.sablona.list.invalidate();
    } catch {
      toast.error(
        t("mealLibrary:sablona.deleteError", "Failed to delete template")
      );
    } finally {
      setDeleteId(null);
    }
  };

  const openEdit = (tmpl: (typeof templates)[number]) => {
    const tags =
      (templateTagsMap as Record<number, { id: number }[]>)[tmpl.id] ?? [];
    setEditTemplate({
      id: tmpl.id,
      nazov: tmpl.nazov,
      poznamka: tmpl.poznamka,
      klientId: tmpl.klientId,
      jeVerejny: tmpl.jeVerejny,
      items: tmpl.items.map(i => ({
        potravinaId: i.potravinaId,
        nazov: i.potravina.nazov,
        hmotnost: i.hmotnost ?? "100",
      })),
      tagIds: tags.map(tg => tg.id),
    });
    setFormOpen(true);
  };

  const openCreate = () => {
    if (!canCreateTemplates) return;
    setEditTemplate(null);
    setFormOpen(true);
  };

  const toggleTagFilter = (tagId: number) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  // Filtered templates based on search text and selected tags
  const filteredTemplates = useMemo(() => {
    return templates.filter(tmpl => {
      const matchesSearch =
        !searchText ||
        tmpl.nazov.toLowerCase().includes(searchText.toLowerCase()) ||
        (tmpl.poznamka ?? "").toLowerCase().includes(searchText.toLowerCase());
      const tmplTags =
        (templateTagsMap as Record<number, { id: number }[]>)[tmpl.id] ?? [];
      const tmplTagIds = tmplTags.map(tg => tg.id);
      const matchesTags =
        selectedTagIds.length === 0 ||
        selectedTagIds.every(id => tmplTagIds.includes(id));
      return matchesSearch && matchesTags;
    });
  }, [templates, searchText, selectedTagIds, templateTagsMap]);

  return (
    <DashboardLayout>
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <BookOpen className="h-7 w-7" />
              {t("mealLibrary:sablona.pageTitle", "Meal Templates")}
            </h1>
            <p className="text-muted-foreground">
              {t(
                "mealLibrary:sablona.pageSubtitle",
                "Pre-configured food blocks that can be applied to client intake"
              )}
            </p>
          </div>
          {canCreateTemplates && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-2" />
              {t("mealLibrary:sablona.new", "New Template")}
            </Button>
          )}
        </div>

        {/* Search + Tag filter bar */}
        <div className="mb-4 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={t(
                "mealLibrary:sablona.searchPlaceholder",
                "Search templates…"
              )}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => setSearchText("")}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <Tag className="h-3.5 w-3.5 text-muted-foreground" />
              {allTags.map(tag => {
                const active = selectedTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTagFilter(tag.id)}
                    className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:border-primary"
                    }`}
                    style={
                      active && tag.farba
                        ? {
                            backgroundColor: tag.farba,
                            borderColor: tag.farba,
                            color: "#fff",
                          }
                        : tag.farba && !active
                          ? { borderColor: tag.farba, color: tag.farba }
                          : undefined
                    }
                  >
                    {tag.nazov}
                  </button>
                );
              })}
              {selectedTagIds.length > 0 && (
                <button
                  className="text-xs text-muted-foreground underline ml-1"
                  onClick={() => setSelectedTagIds([])}
                >
                  {t("common.clearFilters", "Clear")}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Template list */}
        {isLoading ? (
          <p className="text-muted-foreground">
            {t("common.loading", "Loading…")}
          </p>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg">
              {templates.length === 0
                ? t("mealLibrary:sablona.noTemplates", "No meal templates yet.")
                : t(
                    "mealLibrary:sablona.noResults",
                    "No templates match your search."
                  )}
            </p>
            {templates.length === 0 && (
              <p className="text-sm mt-1">
                {t(
                  "mealLibrary:sablona.noTemplatesHint",
                  "Create your first template to speed up client intake logging."
                )}
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map(tmpl => {
              const tmplTags =
                (
                  templateTagsMap as Record<
                    number,
                    { id: number; nazov: string; farba: string | null }[]
                  >
                )[tmpl.id] ?? [];
              const canEditTemplate =
                canManageGlobalTemplates ||
                (canWriteOwnTemplates &&
                  tmpl.klientId != null &&
                  tmpl.klientId === currentClientId);
              return (
                <Card key={tmpl.id} className="flex flex-col">
                  <CardHeader className="flex flex-row items-start justify-between pb-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base leading-tight">
                        {tmpl.nazov}
                      </CardTitle>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {tmpl.klientId == null ? (
                          <Badge variant="secondary" className="text-xs">
                            {t("mealLibrary:sablona.global", "Global")}
                          </Badge>
                        ) : (
                          <Badge
                            variant={tmpl.jeVerejny ? "default" : "outline"}
                            className="text-xs"
                          >
                            {tmpl.jeVerejny
                              ? t("mealLibrary:sablona.public", "Public")
                              : t("mealLibrary:sablona.private", "Private")}
                          </Badge>
                        )}
                      </div>
                      {tmplTags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {tmplTags.map(tg => (
                            <span
                              key={tg.id}
                              className="px-1.5 py-0 rounded-full text-xs border"
                              style={
                                tg.farba
                                  ? { borderColor: tg.farba, color: tg.farba }
                                  : undefined
                              }
                            >
                              {tg.nazov}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {canEditTemplate && (
                      <div className="flex gap-1 shrink-0 ml-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEdit(tmpl)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(tmpl.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="flex-1">
                    {tmpl.poznamka && (
                      <p className="text-xs text-muted-foreground mb-2">
                        {tmpl.poznamka}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {tmpl.items.map(item => (
                        <Badge
                          key={item.id}
                          variant="secondary"
                          className="text-xs"
                        >
                          {item.potravina.nazov}
                          {item.hmotnost && (
                            <span className="ml-1 text-muted-foreground">
                              {item.hmotnost}g
                            </span>
                          )}
                        </Badge>
                      ))}
                      {tmpl.items.length === 0 && (
                        <span className="text-xs text-muted-foreground italic">
                          {t("mealLibrary:sablona.noItems", "No items")}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create/Edit dialog */}
        <TemplateFormDialog
          key={editTemplate?.id ?? `create-${Date.now()}`}
          open={formOpen}
          onOpenChange={setFormOpen}
          editId={editTemplate?.id}
          initialNazov={editTemplate?.nazov ?? ""}
          initialPoznamka={editTemplate?.poznamka ?? ""}
          initialJeVerejny={editTemplate?.jeVerejny ?? canManageGlobalTemplates}
          initialItems={editTemplate?.items ?? []}
          initialTagIds={editTemplate?.tagIds ?? []}
          potraviny={potraviny}
          allTags={allTags}
          canManageTags={canManageTags}
          canManageGlobalTemplates={canManageGlobalTemplates}
          onSaved={() => utils.sablona.list.invalidate()}
        />

        {/* Delete confirmation */}
        <AlertDialog
          open={deleteId !== null}
          onOpenChange={v => !v && setDeleteId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(
                  "mealLibrary:sablona.deleteConfirmTitle",
                  "Delete template?"
                )}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  "mealLibrary:sablona.deleteConfirmDesc",
                  "This will permanently delete the template and all its items."
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("dialog.cancel", "Cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("dialog.delete", "Delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
